import {
  ENTITY_ARRAYS,
  KEYED_OBJECTS,
  stripLinkedSectionEntities,
  stripLinkedSectionTriggers,
} from './diffEngine';
import { byId } from '../store/lookups';

// Clés modifiées localement qui restent à envoyer (au serveur, ou aux
// co-éditeurs après une coupure). On garde les clés, pas les diffs : à l'envoi,
// le patch est reconstruit depuis l'état courant du store — upsert si l'entité
// existe, delete sinon. Renvoyer l'état courant d'une entité est idempotent
// côté back (ProposalContentPatcher : upsert/delete par id, ordre et valeurs
// remplacés en bloc) : les envois successifs se regroupent sans jamais rejouer
// un état périmé, et une entité modifiée entre-temps par un co-éditeur part
// avec sa version reçue.
export const createPendingKeys = () => ({
  entities: {}, // { boxes: Set(id), ... }
  keyed: {}, // { variables: Set(key), triggers: Set(key) }
  order: new Set(), // collections dont l'ordre a changé
  values: new Set(), // collections remplacées en bloc
});

const addTo = (bucket, key, ids) => {
  if (!ids.length) return;
  if (!bucket[key]) bucket[key] = new Set();
  ids.forEach((id) => bucket[key].add(id));
};

// Ajoute à `pending` les clés touchées par un diff `forward` (format diffEngine).
export const addDiffKeys = (pending, diff) => {
  ENTITY_ARRAYS.forEach((key) => {
    addTo(pending.entities, key, [
      ...(diff.upserts?.[key] ?? []).map((entity) => entity.id),
      ...(diff.deletes?.[key] ?? []),
    ]);
  });
  KEYED_OBJECTS.forEach((key) => {
    addTo(pending.keyed, key, [
      ...Object.keys(diff.upserts?.[key] ?? {}),
      ...(diff.deletes?.[key] ?? []),
    ]);
  });
  Object.keys(diff.order ?? {}).forEach((key) => pending.order.add(key));
  Object.keys(diff.values ?? {}).forEach((key) => pending.values.add(key));
};

export const mergePendingKeys = (into, from) => {
  Object.entries(from.entities).forEach(([key, ids]) =>
    addTo(into.entities, key, [...ids])
  );
  Object.entries(from.keyed).forEach(([key, keys]) =>
    addTo(into.keyed, key, [...keys])
  );
  from.order.forEach((key) => into.order.add(key));
  from.values.forEach((key) => into.values.add(key));
};

export const isPendingEmpty = (pending) =>
  Object.keys(pending.entities).length === 0 &&
  Object.keys(pending.keyed).length === 0 &&
  pending.order.size === 0 &&
  pending.values.size === 0;

// Reconstruit le diff `forward` des clés en attente depuis l'état courant.
// Mêmes filtres que computeDiff : une entité de section liée n'est jamais
// envoyée, comme si elle n'existait pas. Null si rien à envoyer.
export const materializeDiff = (pending, state) => {
  const diff = { upserts: {}, deletes: {}, order: {}, values: {} };

  Object.entries(pending.entities).forEach(([key, ids]) => {
    const current = byId(stripLinkedSectionEntities(key, state[key] ?? []));
    const upserts = [];
    const deletes = [];
    ids.forEach((id) => {
      if (current.has(id)) upserts.push(current.get(id));
      else deletes.push(id);
    });
    if (upserts.length) diff.upserts[key] = upserts;
    if (deletes.length) diff.deletes[key] = deletes;
  });

  Object.entries(pending.keyed).forEach(([key, keys]) => {
    const raw = state[key] ?? {};
    const current = key === 'triggers' ? stripLinkedSectionTriggers(raw) : raw;
    const upserts = {};
    const deletes = [];
    keys.forEach((k) => {
      if (k in current) upserts[k] = current[k];
      else deletes.push(k);
    });
    if (Object.keys(upserts).length) diff.upserts[key] = upserts;
    if (deletes.length) diff.deletes[key] = deletes;
  });

  pending.order.forEach((key) => {
    diff.order[key] = stripLinkedSectionEntities(key, state[key] ?? []).map(
      (entity) => entity.id
    );
  });
  pending.values.forEach((key) => {
    diff.values[key] = state[key];
  });

  const isEmpty = Object.values(diff).every(
    (part) => Object.keys(part).length === 0
  );
  return isEmpty ? null : diff;
};
